<template>
  <div v-if="isInCurationMode" class="curation-panel-container">
    <ion-button v-if="!isExpanded && !levelEnded" @click="isExpanded = true" fill="clear" class="show-button">
      <ion-icon :icon="starOutline" slot="icon-only"></ion-icon>
    </ion-button>

    <ion-card v-if="isExpanded || levelEnded">
      <ion-card-header :class="{ 'level-ended': levelEnded }">
        <ion-card-title>
          <template v-if="levelEnded === 'win'">Level Complete!</template>
          <template v-else-if="levelEnded === 'lose'">Level Failed</template>
          <template v-else>Curate Level</template>
        </ion-card-title>
        <ion-button v-if="!levelEnded" @click="isExpanded = false" fill="clear" class="close-button">
          <ion-icon :icon="closeOutline" slot="icon-only"></ion-icon>
        </ion-button>
      </ion-card-header>
      <ion-card-content>
        <div class="candidate-info">
          <small>{{ currentCandidateId }}</small>
        </div>

        <div class="status-buttons">
          <ion-button
            @click="setStatus('keep')"
            :fill="currentStatus === 'keep' ? 'solid' : 'outline'"
            color="success"
            size="small"
          >
            Keep (K)
          </ion-button>
          <ion-button
            @click="setStatus('needs_tweaks')"
            :fill="currentStatus === 'needs_tweaks' ? 'solid' : 'outline'"
            color="warning"
            size="small"
          >
            Tweak (T)
          </ion-button>
          <ion-button
            @click="setStatus('rejected')"
            :fill="currentStatus === 'rejected' ? 'solid' : 'outline'"
            color="danger"
            size="small"
          >
            Reject (R)
          </ion-button>
        </div>

        <div class="difficulty-selector">
          <label>Difficulty:</label>
          <div class="difficulty-buttons">
            <ion-button
              v-for="n in 5"
              :key="n"
              @click="setDifficulty(n)"
              :fill="currentDifficulty === n ? 'solid' : 'outline'"
              size="small"
            >
              {{ n }}
            </ion-button>
          </div>
        </div>

        <ion-textarea
          v-model="notes"
          placeholder="Notes (optional)"
          :rows="2"
          class="notes-input"
        ></ion-textarea>

        <div class="action-buttons">
          <ion-button @click="retry" expand="block" size="small" color="tertiary">
            Retry (Space)
          </ion-button>
          <ion-button
            @click="nextCandidate"
            expand="block"
            size="small"
            :disabled="!canProceedToNext"
          >
            Next (N)
          </ion-button>
          <ion-button @click="skipCandidate" expand="block" size="small" fill="outline">
            Skip
          </ion-button>
          <ion-button @click="exitCuration" expand="block" size="small" fill="clear" color="medium">
            Exit Curation
          </ion-button>
        </div>
      </ion-card-content>
    </ion-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, inject } from 'vue';
import {
  IonCard, IonCardHeader, IonCardTitle, IonCardContent,
  IonButton, IonTextarea, IonIcon,
} from '@ionic/vue';
import { starOutline, closeOutline } from 'ionicons/icons';
import { GameEvents } from '../../../lib/GameEvents';

const DEV_SERVER_URL = 'http://localhost:3001';

const game = inject('game');
const isExpanded = ref(true);
const currentStatus = ref(null);
const currentDifficulty = ref(null);
const notes = ref('');

// Derived from curation slice — all managed by CurationMode
const isInCurationMode = computed(() => !!game.state.curation?.currentCandidateId);
const currentCandidateId = computed(() => game.state.curation?.currentCandidateId || '');
const levelEnded = computed(() => game.state.curation?.levelEnded || null);
const canProceedToNext = computed(() => currentStatus.value !== null);

// When candidate changes, populate form from manifest's cached curation
watch(() => game.state.curation?.currentCandidateId, (newId) => {
  if (!newId) {
    currentStatus.value = null;
    currentDifficulty.value = null;
    notes.value = '';
    return;
  }
  const manifest = game.state.curation?.candidateManifest;
  const candidate = manifest?.candidates?.find(c => c.id === newId);
  const existing = candidate?.curation;
  if (existing) {
    currentStatus.value = existing.status || null;
    currentDifficulty.value = existing.difficulty || null;
    notes.value = existing.notes || '';
  } else {
    currentStatus.value = null;
    currentDifficulty.value = null;
    notes.value = '';
  }
});

const setStatus = async (status) => {
  currentStatus.value = status;
  await saveCuration();
};

const setDifficulty = async (difficulty) => {
  currentDifficulty.value = difficulty;
  if (currentStatus.value) {
    await saveCuration();
  }
};

const saveCuration = async () => {
  if (!currentCandidateId.value || !currentStatus.value) return;

  const curationEntry = {
    status: currentStatus.value,
    difficulty: currentDifficulty.value,
    notes: notes.value || undefined,
    curatedAt: new Date().toISOString(),
  };

  try {
    await fetch(`${DEV_SERVER_URL}/api/curation/${currentCandidateId.value}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(curationEntry),
    });

    // Update manifest cache so next candidate selection sees this curation
    game.emit(GameEvents.CURATION_SAVED, { candidateId: currentCandidateId.value, curation: curationEntry });
  } catch (e) {
    console.error('Failed to save curation:', e);
  }
};

const retry = () => game.dispatch(GameEvents.RESTART_GAME);
const nextCandidate = () => { if (canProceedToNext.value) game.emit(GameEvents.CURATION_NEXT_CANDIDATE); };
const skipCandidate = () => game.emit(GameEvents.CURATION_NEXT_CANDIDATE);
const exitCuration = () => game.emit(GameEvents.EXIT_CURATION);

// Keyboard shortcuts
const handleKeydown = (e) => {
  if (!isInCurationMode.value) return;
  if ((e.target).tagName === 'TEXTAREA' || (e.target).tagName === 'INPUT') return;

  switch (e.key.toLowerCase()) {
    case 'k': setStatus('keep'); break;
    case 't': setStatus('needs_tweaks'); break;
    case 'r': setStatus('rejected'); break;
    case '1': case '2': case '3': case '4': case '5':
      setDifficulty(parseInt(e.key)); break;
    case 'n': if (canProceedToNext.value) nextCandidate(); break;
    case ' ': e.preventDefault(); retry(); break;
  }
};

onMounted(() => { window.addEventListener('keydown', handleKeydown); });
onUnmounted(() => { window.removeEventListener('keydown', handleKeydown); });
</script>

<style scoped>
.curation-panel-container {
  position: fixed;
  bottom: 80px;
  right: 10px;
  z-index: 1000;
  width: 280px;
}

.show-button {
  font-size: 32px;
  color: var(--ion-color-warning);
  --padding-start: 0;
  --padding-end: 0;
  float: right;
}

ion-card {
  --background: rgba(51, 51, 51, 0.95);
  --color: #fff;
  margin: 0;
}

ion-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}

ion-card-title {
  font-size: 1em;
}

ion-card-content {
  padding: 8px 12px;
}

.candidate-info {
  margin-bottom: 8px;
  color: #888;
}

.status-buttons {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
}

.status-buttons ion-button {
  flex: 1;
  font-size: 0.8em;
}

.difficulty-selector {
  margin-bottom: 12px;
}

.difficulty-selector label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.9em;
  color: #aaa;
}

.difficulty-buttons {
  display: flex;
  gap: 4px;
}

.difficulty-buttons ion-button {
  flex: 1;
  --padding-start: 8px;
  --padding-end: 8px;
}

.notes-input {
  --background: rgba(255, 255, 255, 0.1);
  --color: #fff;
  --placeholder-color: #888;
  margin-bottom: 12px;
  font-size: 0.9em;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-buttons ion-button {
  margin: 0;
}

ion-card-header.level-ended {
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid var(--ion-color-primary);
}

ion-card-header.level-ended ion-card-title {
  font-weight: bold;
  color: var(--ion-color-primary);
}
</style>
